import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import { Grid, Tooltip, Button, Dialog, DialogTitle, Box, DialogContent, DialogActions, TextField, Autocomplete, CircularProgress, Checkbox, FormGroup, FormControlLabel, Typography, FormLabel, RadioGroup, Radio, InputAdornment } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import SvClaimDetails from './SvClaimDetails';
import { addOpeningBalanceApi, getConstantApi, getCurrencyApi, getEngineLLPApi, getMaintenanceEventApi, getMaintenanceSVEventApi, getMajorAssemblyApi, updateOpeningBalanceApi, } from '../apiService';
import { assetTypeMap } from '../../leases/components/LeaseConstants';
import { assetType, backendDateFormat, fieldDateFormat } from '../../../constants';
import { checkApiStatus, numberWithCommas } from '../../../utils';
import { globalGetService, globalPostService, globalPutService } from '../../../utils/globalApiServices';
import { AttachementsUpload } from '../../../shared_elements/components';
import { regexConstants } from '../../../constants/regEx';
import { setFileUploadDetails } from '../../../shared_elements/actions';
import { formatBytes } from '../../../utils/fileUpload';
import { trackActivity } from '../../../utils/mixpanel';
import { PageLoader } from '../../../shared_elements';
import LessMoreTextFieldComp from '../../../shared_elements/components/LessMoreTextFieldComp';

const OpeningBalanceCRU = ({ onToggleModal, onOpenAttachments, fileUpload, modeType, params, addEditOpeningBalance, getResponseBack }) => {
    const dispatch = useDispatch()
    const {leaseBasicDetails, storageDetails } = useSelector((state) => state.leases);
    const { enqueueSnackbar } = useSnackbar();
    const [openingBalance, setOpeningBalance] = useState(addEditOpeningBalance?.data);
    const [isLoading, setLoading] = useState({ skeletonLoader: false, pageLoader: false, buttonLoader: false });
    const toggleLoader = (type, status) => { setLoading({ ...isLoading, [type]: status }) }
    const [error, setError] = useState({});
    const [pltConstants, setPltConstants] = useState({ constant_types: [], asset_types: [], });
    const [dropDownLoader, setDropDownLoader] = useState({ currency: false, major_assembly: false, maintenance_events: false })
    const [attachedFiles, setAttachedFiles] = useState([]);

    const getPltConstants = () => {
        setDropDownLoader(prev => ({ ...prev, account_type: true }));
        globalPostService(getConstantApi(), { constant_types: ["claim_type", "claim_event_type"] })
            .then(response => {
                setDropDownLoader(prev => ({ ...prev, account_type: false }));
                if (checkApiStatus(response)) {
                    setPltConstants(prevConstants => ({
                        ...prevConstants,
                        constant_types: response.data.data || [],
                    }));
                }
            })
    }

    const getMajorAssembly = () => {
        setDropDownLoader(prev => ({ ...prev, major_assembly: true }));
        globalGetService(getMajorAssemblyApi(params))
            .then(response => {
                setDropDownLoader(prev => ({ ...prev, major_assembly: false }));
                if (checkApiStatus(response)) {
                    setPltConstants(prevConstants => ({
                        ...prevConstants,
                        asset_types: response?.data?.data || []
                    }));
                } else {
                    enqueueSnackbar(response?.data?.message, { variant: "error", anchorOrigin: { vertical: "top", horizontal: "right" }, })
                }
            })
    }

    const getMaintenanceEvent = (assetData) => {
        setDropDownLoader(prev => ({ ...prev, maintenance_events: true }));
        globalGetService(
         getMaintenanceSVEventApi(assetType[assetData?.asset_type], assetData?.asset_id ? assetData?.asset_id : assetData?.id))
            .then(response => {
                setDropDownLoader(prev => ({ ...prev, maintenance_events: false }));
                if (checkApiStatus(response)) {
                    const updatedMaintenanceEvents = response?.data?.data[0]?.maintenance_events;
                    setPltConstants(prevConstants => ({ ...prevConstants, maintenance_events: updatedMaintenanceEvents || [], }));
                }
            })
    }
    const getCurrency = () => {
        setDropDownLoader(prev => ({ ...prev, currency: true }));
        globalGetService(
            getCurrencyApi())
            .then(response => {
                setDropDownLoader(prev => ({ ...prev, currency: false }));
                if (checkApiStatus(response)) {
                    setPltConstants(prevConstants => ({ ...prevConstants, currencies: response?.data?.data || [], }));
                }
            })
    }
    const getEngineLLPs = async (assetType, assetSlug, query) => {
        toggleLoader("skeletonLoader", true)
        return globalGetService(getEngineLLPApi(assetType, assetSlug, query))
            .then(response => {
                if (checkApiStatus(response)) {
                    toggleLoader("skeletonLoader", false)
                    if (query === "module") {
                        const data = response?.data?.data;
                        const llpItems = (data?.llp ?? []).map((ele, index) => ({
                            module_name: ele,
                            fund_balance: 0,
                        }));
                        const nonLlpItems = (data?.non_llp ?? []).map((ele, index) => ({
                            module_name: ele,
                            fund_balance: 0,
                            is_non_llp: true,
                        }));
                        return [...llpItems, ...nonLlpItems];
                    } else {
                        const data = response?.data?.data;
                        const processGroup = (groupData, isNonLlp) => {
                            const seen = {};
                            const cleanedData = {};
                            Object.keys(groupData ?? {}).forEach(module => {
                                const uniqueParts = [];
                                groupData[module]?.forEach(part => {
                                    if (!seen[part]) {
                                        seen[part] = true;
                                        uniqueParts.push(part);
                                    }
                                });
                                cleanedData[module] = uniqueParts;
                            });
                            return Object.entries(cleanedData).flatMap(([module, descriptions]) =>
                                descriptions.map((description, index) => ({
                                    module_name: module,
                                    fund_balance: 0,
                                    description,
                                    ...(isNonLlp ? { is_non_llp: true } : {}),
                                }))
                            );
                        };
                        return [
                            ...processGroup(data?.llp, false),
                            ...processGroup(data?.non_llp, true),
                        ];
                    }
                } else {
                    enqueueSnackbar(response?.data?.message, { variant: "error", anchorOrigin: { vertical: "top", horizontal: "right" }, })
                    toggleLoader("skeletonLoader", false)
                }
                return [];
            });
    };

    const onFieldChange = (key, value) => {
        const isEdit = (modeType === "edit");
        const updatedData = addEditOpeningBalance?.data;
        const isSame = (field, compareVal = value?.value) => isEdit && updatedData?.[field]?.value === compareVal;
        const updatedValues = { [key]: value, };
        if (key === "major_assembly") {
            if (value?.id && value?.asset_type && dropDownLoader?.major_assembly === false) { getMaintenanceEvent(value) }
            updatedValues.mainteance_program_id = value?.asset_slug === updatedData?.major_assembly?.asset_slug ? updatedData?.mainteance_program_id : null;
        }
        else if (key === "claim_type" && openingBalance?.major_assembly?.asset_type === 2) {
            const sameClaimType = isSame("claim_type")
            const isRestorable = updatedData?.major_assembly?.asset_slug === openingBalance?.major_assembly?.asset_slug && sameClaimType;
            updatedValues.amount = isRestorable ? updatedData?.amount : openingBalance?.amount;
            updatedValues.claim_event_type = isRestorable ? updatedData?.claim_event_type : { value: 1, label: "Fund" };
            updatedValues.opening_balance_engine_split_details = isRestorable ? updatedData?.opening_balance_engine_split_details : []
            onFocusChange("amount")
        } else if (key === "claim_event_type" && openingBalance?.major_assembly?.asset_type === 2) {
            const sameMajorAssembly = updatedData?.major_assembly?.asset_slug === openingBalance?.major_assembly?.asset_slug;
            const sameClaimType = updatedData?.claim_type?.value === openingBalance?.claim_type?.value;
            const isRestorable = isSame("claim_event_type") && sameMajorAssembly && sameClaimType;
            updatedValues.amount = sameMajorAssembly && value?.value===1 && sameClaimType  ? updatedData?.amount : "";
            onFocusChange("amount")
            if (openingBalance?.major_assembly?.asset_slug && (value?.value === 2 || value?.value === 3)) {
                if (isRestorable) {
                    updatedValues.opening_balance_engine_split_details = updatedData?.opening_balance_engine_split_details;
                }
                else if (openingBalance?.claim_type?.value === 2 && value?.value === 3) {
                    updatedValues.opening_balance_engine_split_details = [];
                    getEngineLLPs(assetTypeMap[openingBalance?.major_assembly?.asset_type], openingBalance?.major_assembly?.asset_slug, "description").then(llpData => {
                        updatedValues.opening_balance_engine_split_details = llpData;
                        setOpeningBalance({
                            ...openingBalance,
                            ...updatedValues,
                        });
                    })
                }
                else {
                    updatedValues.opening_balance_engine_split_details = [];
                    getEngineLLPs(assetTypeMap[openingBalance?.major_assembly?.asset_type], openingBalance?.major_assembly?.asset_slug, "module").then(llpData => {
                        updatedValues.opening_balance_engine_split_details = llpData;
                        setOpeningBalance({
                            ...openingBalance,
                            ...updatedValues,
                        });
                    })
                }
            }
        }
        setOpeningBalance({
            ...openingBalance,
            ...updatedValues,
        });
    }
    const onFocusChange = (key) => { setError({ ...error, [key]: "" }) }

    const onMultiFieldChange = (arrKey, rowId, field, value, allowKey) => {
        const updatedArray = (openingBalance && arrKey && [...openingBalance[arrKey]]) || [];
        const parsedValue =value !== ""? Number(value): value;
        const newData = updatedArray.map((row,rowIndex )=>rowIndex === rowId? { ...row, [field]: parsedValue }: row);
        setOpeningBalance(prev => ({
          ...prev,
          [arrKey]: newData,
        }));
      };

    const handleFileChange = (files) => {
        setAttachedFiles(files);
        dispatch(setFileUploadDetails({ attachments: files }))
    };

    const onAddEditOpeningBalance = () => {
        let validationInputs = {};
        validationInputs = {
            major_assembly: !openingBalance?.major_assembly?.name ? "Please select Major Assembly" : "",
            mainteance_program_id: !openingBalance?.mainteance_program_id ? "Please select Maintenance Event" : "",
            currency: !openingBalance?.currency ? "Please select Currency" : "",
            amount:openingBalance?.claim_event_type?.value ===1 && !openingBalance?.amount  ? "Please enter Amount" : "" ,
            fund_balance_as_of: !openingBalance?.fund_balance_as_of ? "Please select Fund As Of" :  "",
        };
        if (openingBalance?.fund_balance_as_of) {
            if (!moment(openingBalance.fund_balance_as_of).isValid()) { validationInputs.fund_balance_as_of = "Please select valid Fund As Of date"; }
            else if ( moment(openingBalance.fund_balance_as_of).isBefore(moment(leaseBasicDetails?.start_date))) { validationInputs.fund_balance_as_of = "Fund As Of cannot be before lease start date"; }
            else if ( moment(openingBalance.fund_balance_as_of).isAfter(moment(leaseBasicDetails?.end_date))) { validationInputs.fund_balance_as_of = "Fund As Of cannot be after lease end date"; }
            else { validationInputs.fund_balance_as_of = ""; }
        }
       
        if (Object.keys(validationInputs).every((k) => { return validationInputs[k] === ""; }) ) {
            const payload = {
                ...openingBalance,
                asset_type: Number(openingBalance?.major_assembly?.asset_type),
                asset_id: Number(openingBalance?.major_assembly?.asset_id || openingBalance?.major_assembly?.id),
                major_assembly: { asset_slug: openingBalance?.major_assembly?.asset_slug, asset_type: Number(openingBalance?.major_assembly?.asset_type), asset_id: Number(openingBalance?.major_assembly?.asset_id || openingBalance?.major_assembly?.id), name: openingBalance?.major_assembly?.asset_type === 1 ? openingBalance?.major_assembly?.payloadName : (openingBalance?.major_assembly?.asset_name || openingBalance?.major_assembly?.name) },
                mainteance_program_id: openingBalance?.mainteance_program_id ? openingBalance?.mainteance_program_id : null,
                currency: openingBalance?.currency ? modeType==="edit" ?openingBalance?.currency :  openingBalance?.currency?.code : null,
                amount : openingBalance?.amount ? Number(openingBalance?.amount)  : 0,
                claim_type: openingBalance?.major_assembly?.asset_type === 2 ? openingBalance?.claim_type?.value : { value: 1, label: "PRSV" },
                claim_event_type: openingBalance?.major_assembly?.asset_type === 2 ? openingBalance?.claim_event_type?.value : { value: 1, label: "Fund" },
                opening_balance_engine_split_details: openingBalance?.major_assembly?.asset_type === 2 ? openingBalance?.opening_balance_engine_split_details : [],
                claim_approval_period : openingBalance?.claim_approval_period ?  openingBalance?.claim_approval_period :null
            };
            if(openingBalance?.major_assembly?.asset_type !== 2){
               delete payload.claim_type
               delete payload.claim_event_type
            }
            setLoading({ pageLoader: true });
            if (openingBalance?.id) {
                globalPutService(updateOpeningBalanceApi(({ ...params, slug: openingBalance?.slug })), payload)
                    .then(async (response) => {
                        setLoading({ pageLoader: false });
                        if (checkApiStatus(response)) {
                            if (attachedFiles?.length > 0) {
                                fileUpload(openingBalance.slug, 'updated', attachedFiles, getResponseBack,)
                            }
                            onToggleModal();
                            getResponseBack();
                            enqueueSnackbar(response?.data?.message, { variant: "success", anchorOrigin: { horizontal: "right", vertical: "top" }, });
                            trackActivity('Item Edited', { page_title: `Edit Opening Balance`, success_msg: response?.data?.message, item_id: openingBalance?.id, payload: payload, response: response?.data })
                        } else {
                            enqueueSnackbar(response.data.message, { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" }, });
                            trackActivity('Item Edited', { page_title: `Edit Opening Balance`, error_msg: response?.data?.message, item_id: openingBalance?.id, payload: payload, response: response?.data })
                        }
                    })
            } else {
                globalPostService(addOpeningBalanceApi(params), payload).then(async (response) => {
                    setLoading({ pageLoader: false });
                    if (checkApiStatus(response)) {
                        if (attachedFiles?.length > 0) {
                            fileUpload(response?.data?.data?.slug, 'created', attachedFiles, getResponseBack)
                        }
                        onToggleModal();
                        getResponseBack();
                        enqueueSnackbar(response?.data?.message, { variant: "success", anchorOrigin: { horizontal: "right", vertical: "top" }, });
                        trackActivity('Item Added', { page_title: `Add Opening Balance`, success_msg: response?.data?.message, item_id: response?.data?.data?.id, payload: payload, response: response?.data })
                    } else {
                        enqueueSnackbar(response?.data?.message, { variant: "error", anchorOrigin: { vertical: "top", horizontal: "right" }, })
                        trackActivity('Item Added', { page_title: `Add Opening Balance`, error_msg: response?.data?.message, item_id: null, payload: payload, response: response?.data })
                    }
                })
            }
        } else {
            setError(validationInputs)
            trackActivity(`Item ${modeType === 'add' ? 'Added' : 'Edited'}`, { page_title: `${modeType === 'add' ? 'Add' : modeType === 'edit' ? 'Edit' : 'View'} Opening Balance`, error_msg: validationInputs, })
        }
    }
    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: `${modeType === 'add' ? 'Add' : modeType === 'edit' ? 'Edit' : 'View'} Opening Balance`, page_type: page_type, })
    }
    useEffect(() => { getMajorAssembly(); getCurrency(); getPltConstants(); if(openingBalance?.major_assembly?.asset_id) getMaintenanceEvent(openingBalance?.major_assembly) }, [])
    const isFundBalanceTotalMode = openingBalance?.major_assembly?.asset_type === 2 && [2, 3].includes(openingBalance?.claim_event_type?.value);
    const fundBalanceTotal = (openingBalance?.opening_balance_engine_split_details || []).reduce((total, item) => total + (Number(item?.fund_balance) || 0), 0);
    return (
        <>
            <Dialog
                aria-labelledby="customized-dialog-title"
                className='add-lease-dialog'
                maxWidth='md'
                fullWidth
                open={addEditOpeningBalance?.modal}
            >
                <DialogTitle sx={{ m: 0, p: 2 }} className='dialog-header'>
                    <Typography className="dialog-header-title">{modeType === 'add' ? 'Add Opening Balance' : modeType === 'edit' ? 'Edit Opening Balance' : 'View Opening Balance'}</Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <Autocomplete
                                disablePortal
                                disableClearable
                                options={pltConstants?.asset_types}
                                isOptionEqualToValue={(option, value) => option.id === value.asset_id}
                                disabled={modeType === 'view' || dropDownLoader?.major_assembly}
                                getOptionLabel={(option) => `${option?.asset_name || ''} ${option?.name || ''}` || ""}
                                noOptionsText={dropDownLoader?.major_assembly ? "loading" : "No Option Available"}
                                value={openingBalance?.major_assembly || null}
                                onChange={(e, value) => onFieldChange("major_assembly", value)}
                                onFocus={() => onFocusChange("major_assembly")}
                                renderInput={(params) => {
                                    const selectedLabel = params?.inputProps?.value || "";
                                    return (
                                        <Tooltip title={selectedLabel.length > 23 ? selectedLabel : ""} placement="bottom" arrow>
                                            <TextField
                                                required
                                                {...params}
                                                label="Major Assembly"
                                                margin='normal'
                                                size='small'
                                                error={Boolean(error.major_assembly)}
                                                helperText={error.major_assembly}
                                                disabled={modeType === 'view'}
                                                slotProps={{
                                                    inputLabel: {
                                                        shrink: true,
                                                    },
                                                    input: {
                                                        ...params?.InputProps, endAdornment: (<>{dropDownLoader?.major_assembly ? (<CircularProgress color="inherit" size={20} />) : null}
                                                            {params?.InputProps?.endAdornment}
                                                        </>
                                                        )
                                                    }
                                                }}
                                            />
                                        </Tooltip>)
                                }}
                            />
                        </Grid>

                        <Grid size={{ md: 4, xs: 12 }}>
                            <Autocomplete
                                disablePortal
                                disableClearable
                                options={pltConstants?.maintenance_events || []}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                noOptionsText={dropDownLoader?.maintenance_events ? "loading" : "No Option Available"}
                                disabled={modeType === 'view' || dropDownLoader?.maintenance_events}
                                getOptionLabel={(option) => option.name || ""}
                                value={openingBalance?.mainteance_program_id || null}
                                onChange={(e, value) => onFieldChange("mainteance_program_id", value)}
                                onFocus={() => onFocusChange("mainteance_program_id")}
                                renderInput={(params) => {
                                    const selectedLabel = params?.inputProps?.value || "";
                                    return (
                                        <Tooltip title={selectedLabel.length > 23 ? selectedLabel : ""} placement="bottom" arrow >
                                            <TextField
                                                required
                                                {...params}
                                                label="Maintenance Event"
                                                margin='normal'
                                                size='small'
                                                error={Boolean(error.mainteance_program_id)}
                                                helperText={error.mainteance_program_id}
                                                disabled={modeType === 'view' || dropDownLoader?.maintenance_events}
                                                slotProps={{
                                                    inputLabel: {
                                                        shrink: true,
                                                    },
                                                    input: {
                                                        ...params?.InputProps, endAdornment: (<>{dropDownLoader?.maintenance_events ? (<CircularProgress color="inherit" size={20} />) : null}
                                                            {params?.InputProps?.endAdornment}
                                                        </>
                                                        )
                                                    }
                                                }}
                                            />
                                        </Tooltip>)
                                }}
                            />
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <Autocomplete
                                disablePortal
                                disableClearable
                                options={pltConstants?.currencies || []}
                                disabled={modeType === 'view'}
                                getOptionLabel={(option) => option.name || ""}
                                value={openingBalance?.currency || null}
                                onChange={(e, value) => onFieldChange("currency", value)}
                                noOptionsText={dropDownLoader?.currency ? "loading" : "No Option Available"}
                                onFocus={() => onFocusChange("currency")}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderInput={(params) => (
                                    <TextField
                                        required
                                        {...params}
                                        label="Currency"
                                        margin='normal'
                                        size='small'
                                        error={Boolean(error.currency)}
                                        helperText={error.currency}
                                        disabled={modeType === 'view'}
                                        slotProps={{
                                            inputLabel: {
                                                shrink: true,
                                            },
                                            input: {
                                                ...params?.InputProps, endAdornment: (<>{dropDownLoader?.currency ? (<CircularProgress color="inherit" size={20} />) : null}
                                                    {params?.InputProps?.endAdornment}
                                                </>
                                                )
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <TextField
                                required
                                id="amount"
                                value={(modeType === 'view' || isFundBalanceTotalMode) && (isFundBalanceTotalMode ? fundBalanceTotal : openingBalance?.amount) !== '' && (isFundBalanceTotalMode ? fundBalanceTotal : openingBalance?.amount) !== null && (isFundBalanceTotalMode ? fundBalanceTotal : openingBalance?.amount) !== undefined ? numberWithCommas(isFundBalanceTotalMode ? fundBalanceTotal : openingBalance?.amount) : (isFundBalanceTotalMode ? fundBalanceTotal : openingBalance?.amount)}
                                onChange={(e, value) => { regexConstants.decimalnodotstart.test(e.target.value) ? onFieldChange('amount', e.target.value) : e.preventDefault() }}
                                onFocus={() => onFocusChange("amount")}
                                label="Amount"
                                variant="outlined"
                                size="small"
                                margin="normal"
                                error={error.amount}
                                helperText={error.amount}
                                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 11 }, input: { startAdornment: (<InputAdornment position="start">{openingBalance?.currency?.symbol}</InputAdornment>) } }}
                                fullWidth
                                disabled={modeType === 'view' || isFundBalanceTotalMode}
                            />
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    required
                                    label="Fund As Of"
                                    value={openingBalance?.fund_balance_as_of ? moment(openingBalance.fund_balance_as_of) : null}
                                    onChange={(data, value) => {
                                        onFieldChange('fund_balance_as_of', moment(data).format(backendDateFormat))
                                        onFocusChange("fund_balance_as_of")
                                    }}
                                    slotProps={{
                                        textField: {
                                            required: "true",
                                            InputLabelProps: { shrink: true },
                                            size: "small",
                                            margin: "normal",
                                            helperText: error.fund_balance_as_of,
                                            error: error.fund_balance_as_of,
                                            onFocus: () => { onFocusChange("fund_balance_as_of") }
                                        },
                                    }}
                                    onFocus = {()=> onFocusChange("fund_balance_as_of")}
                                    sx={{ width: "100%" }}
                                    format={fieldDateFormat}
                                    disabled={modeType === 'view'}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid size={{ md: 4, xs: 12}}>
                            <TextField
                                id='claim_approval_period'
                                label="Claim Approval Period (Days)"
                                value={openingBalance?.claim_approval_period || ""}
                                onChange={(e) => { regexConstants.onlyNumeric.test(e.target.value) || e.target.value === "" ? onFieldChange("claim_approval_period", e.target.value) : e.preventDefault() }}
                                variant='outlined'
                                size='small'
                                margin='normal'
                                fullWidth
                                disabled={modeType === 'view'}
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                        </Grid>
                        {modeType === 'view' ?
                            <Grid item size={{ md: 12, xs: 12 }}>
                                <LessMoreTextFieldComp label='Remarks' value={openingBalance?.remarks || ""} />
                            </Grid>
                            :
                            <Grid size={{ md: 12, xs: 12 }}>
                                <TextField
                                    id="remarks"
                                    value={openingBalance?.remarks}
                                    onChange={(e, value) => onFieldChange('remarks', e.target.value)}
                                    label="Remarks"
                                    variant="outlined"
                                    size="small"
                                    margin="normal"
                                    slotProps={{ inputLabel: { shrink: true } }}
                                    sx={{ '& .MuiInputBase-input': { resize: 'vertical', overflow: 'auto' } }}
                                    onFocus={() => onFocusChange("remarks")}
                                    fullWidth
                                    rows={3}
                                    multiline
                                    disabled={modeType === 'view'}
                                />
                            </Grid>}
                        {openingBalance?.major_assembly?.asset_type === 2 && <Grid size={{ md: 12, xs: 12 }}>
                            <Typography className='section-header-text'><u>SV Claim Details:</u></Typography>
                            <SvClaimDetails claimData={openingBalance}  isLoading={isLoading} onFieldChange={onFieldChange} modeType={modeType} pltConstants={pltConstants} onMultiFieldChange={onMultiFieldChange} />
                        </Grid>}

                        <Grid size={{ md: 12, xs: 12 }}>
                            <Box className="responsive-buttons">
                                {openingBalance?.id && addEditOpeningBalance?.data?.attachments?.length ?
                                    <Button onClick={() => { onOpenAttachments(); handleItemClicked('ViewAttachments Button') }} size='small' variant='outlined' color='primary' sx={{ marginBottom: '10px' }}>({addEditOpeningBalance?.data?.attachments?.length})Attachment(s)</Button> : 
                                    <Button color='primary' variant='outlined' size='small' sx={{ marginBottom: '10px' }}>Attachments</Button>
                                }
                                <Button className='available-space-btn' size='small' variant='outlined' color='primary' sx={{ marginBottom: '10px' }}>{`Available Space : ${formatBytes(storageDetails?.remaining_storage)}`}</Button>
                            </Box>
                            {modeType === 'view' ? null : <AttachementsUpload onFileChange={handleFileChange} attachments={addEditOpeningBalance?.data?.attachments} remainingStorage={storageDetails?.remaining_storage} />}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions className='dialog-action'>
                    <Button className='cancel-btn' disabled={isLoading?.pageLoader} variant='outlined' size='small' onClick={() => { onToggleModal(); handleItemClicked('Cancel Button') }}>{modeType === "view" ? "Close" : "Cancel"}</Button>
                    {modeType === "view" ? null : <Button variant='contained' className='btn-primary' size='small' onClick={() => { handleItemClicked('Save Button'); onAddEditOpeningBalance(); }}> Save </Button>}
                </DialogActions>
            </Dialog>
            {isLoading?.pageLoader ? <PageLoader /> : null}
        </>
    )
}
export default OpeningBalanceCRU